import { Pressable, Platform } from 'react-native';
import { Menu, MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import {
  MoreHorizontalIcon,
  Cancel01Icon,
} from '@hugeicons-pro/core-solid-rounded';
import {
  Share01Icon,
  PencilEdit02Icon,
  CancelSquareIcon,
  Alert02Icon,
  Delete02Icon,
  NotificationOff03Icon,
  Notification02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { ShareModal } from '@/components/ui/ShareModal';
import { Post } from '@/models/Post';
import { useTranslation } from 'react-i18next';
import { useSupportService } from '@/hooks/useSupportService';
import { ReportReasonSheet } from '@/components/domain/common/ReportReasonSheet';
import { useShare } from '@/hooks/useShare';
import { useState } from 'react';
import { PostsService } from '@/services/domain/PostsService';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { MuteDurationSheet } from './MuteDurationSheet';
import { EditPost } from './EditPost';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from '@/components/gluestack/alert-dialog';
import { Button, ButtonText } from '@/components/gluestack/button';
import { Text } from '@/components/gluestack/text';
import { Heading } from '@/components/gluestack/heading';
import { useToastMessages } from '@/hooks/useToastMessages';

interface IPostContextMenuProps {
  post: Post;
  onPostDeleted?: (postId: number) => void;
  onPostEdited?: (post: Post) => void;
}

export function PostContextMenu({ post, onPostDeleted, onPostEdited }: IPostContextMenuProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { submitPolicyViolationForPost } = useSupportService();
  const { handleShare, handleCopyUrl, handleOpenUrl, showShareModal, setShowShareModal, shareUrl } = useShare();
  const [mutedState, setMutedState] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showMuteDurationSheet, setShowMuteDurationSheet] = useState(false);
  const [showEditPostSheet, setShowEditPostSheet] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const handleMenuOpen = async () => {
    try {
      const response = await PostsService.getMutedState(post.Id);
      setMutedState(response.mutedState);
    } catch (error) {
      showErrorToast(t('post.getMutedState.error'), error);
    }
  };

  const [showReportReasonSheet, setShowReportReasonSheet] = useState(false);

  const handleReport = () => {
    setShowReportReasonSheet(true);
  };

  const handleOpenDeleteConfirmation = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDelete = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PostsService.deletePost(post.Id);
      // Call the callback to refresh the parent component
      onPostDeleted?.(post.Id);
      showSuccessToast(t('post.delete.success'));
    } catch (error) {
      showErrorToast(t('post.delete.error'), error);
    } finally {
      setIsLoading(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleMute = () => {
    setShowMuteDurationSheet(true);
  };

  const handleMuteDurationSelect = async (minutes: number) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PostsService.mutePost(post.Id, minutes);
      setMutedState(true);
      showSuccessToast(t('post.mute.success'));
    } catch (error) {
      showErrorToast(t('post.mute.error'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnmute = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PostsService.unmutePost(post.Id);
      setMutedState(false);
      showSuccessToast(t('post.unmute.success'));
    } catch (error) {
      showErrorToast(t('post.unmute.error'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismissAnnouncement = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await PostsService.hideSticky(post.Id);
      showSuccessToast(t('post.dismissAnnouncement.success'));
      // Call the callback to refresh the parent component
      onPostDeleted?.(post.Id);
    } catch (error) {
      showErrorToast(t('post.dismissAnnouncement.error'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setShowEditPostSheet(true);
  };
  
  const handlePostEdited = (editedPost: Post) => {
    onPostEdited?.(editedPost);
    setShowEditPostSheet(false);
  };

  const handleSharePost = () => {
    handleShare({
      type: 'post',
      id: post.Id,
      userName: post.Member.UserName,
    });
  };



  return (
    <>
      <Menu
        placement="bottom right"
        onOpen={handleMenuOpen}
        trigger={(props: any) => (
          <Pressable
            {...props}
            className="self-start"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <HugeIconsWrapper icon={MoreHorizontalIcon} size={24} className="text-typography-700" />
          </Pressable>
        )}>
        {post.CanEdit && (
          <MenuItem key="edit" textValue={t('post.menu.edit')} onPress={handleEdit}>
            <MenuItemIcon icon={PencilEdit02Icon} />
            <MenuItemLabel>{t('post.menu.edit')}</MenuItemLabel>
          </MenuItem>
        )}
        {post.IsSticky && (
          <MenuItem
            key="dismiss"
            textValue={t('post.menu.dismissAnnouncement')}
            onPress={handleDismissAnnouncement}
            disabled={isLoading}>
            <MenuItemIcon icon={CancelSquareIcon} />
            <MenuItemLabel>{t('post.menu.dismissAnnouncement')}</MenuItemLabel>
          </MenuItem>
        )}
        <MenuItem key="share" textValue={t('post.menu.share')} onPress={handleSharePost}>
          <MenuItemIcon icon={Share01Icon} />
          <MenuItemLabel>{t('post.menu.share')}</MenuItemLabel>
        </MenuItem>
        {mutedState === false && (
          <MenuItem
            key="stopNotifications"
            textValue={t('post.menu.stopNotifications')}
            onPress={handleMute}
            disabled={isLoading}>
            <MenuItemIcon icon={NotificationOff03Icon} />
            <MenuItemLabel>{t('post.menu.stopNotifications')}</MenuItemLabel>
          </MenuItem>
        )}
        {mutedState === true && (
          <MenuItem
            key="resumeNotifications"
            textValue={t('post.menu.resumeNotifications')}
            onPress={handleUnmute}
            disabled={isLoading}>
            <MenuItemIcon icon={Notification02Icon} />
            <MenuItemLabel>{t('post.menu.resumeNotifications')}</MenuItemLabel>
          </MenuItem>
        )}
        {!post.IsAuthor && (
          <MenuItem key="report" textValue={t('post.menu.report')} onPress={handleReport}>
            <MenuItemIcon icon={Alert02Icon} className="color-error-500" />
            <MenuItemLabel>{t('post.menu.report')}</MenuItemLabel>
          </MenuItem>
        )}
        {(post.IsAuthor || (Platform.OS === 'web' && post.CanModerate)) && (
          <MenuItem
            key="delete"
            textValue={t('post.menu.delete')}
            onPress={handleOpenDeleteConfirmation}
            disabled={isLoading}>
            <MenuItemIcon icon={Delete02Icon} className="color-error-500" />
            <MenuItemLabel>{t('post.menu.delete')}</MenuItemLabel>
          </MenuItem>
        )}
      </Menu>

      <ContextSheet
        isOpen={showMuteDurationSheet}
        onClose={() => setShowMuteDurationSheet(false)}
        contentComponent={MuteDurationSheet}
        title={t('post.mute.title')}
        payload={{
          postId: post.Id,
          onDurationSelected: handleMuteDurationSelect,
        }}
      />

      <ContextSheet
        isOpen={showEditPostSheet}
        onClose={() => setShowEditPostSheet(false)}
        contentComponent={EditPost}
        title={t('post.menu.edit')}
        payload={{ post, onPostEdited: handlePostEdited }}
      />

      <AlertDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        size="md">
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading size="md">{t('post.delete.confirmTitle')}</Heading>
            <AlertDialogCloseButton className="">
              <HugeIconsWrapper icon={Cancel01Icon} size={20} className="color-neutral-500" />
            </AlertDialogCloseButton>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3">
            <Text>{t('post.delete.confirmMessage')}</Text>
          </AlertDialogBody>
          <AlertDialogFooter className="mt-6 gap-3">
            <Button
              variant="outline"
              action="neutral"
              onPress={() => setShowDeleteConfirmation(false)}
              disabled={isLoading}>
              <ButtonText>{t('common.cancel')}</ButtonText>
            </Button>
            <Button action="negative" onPress={handleDelete} disabled={isLoading}>
              <ButtonText>{t('common.delete')}</ButtonText>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={shareUrl}
        onCopyUrl={handleCopyUrl}
        onOpenUrl={handleOpenUrl}
        title={t('post.share.shareLink')}
      />
      <ReportReasonSheet
        isOpen={showReportReasonSheet}
        onClose={() => setShowReportReasonSheet(false)}
        // A photo post is reported as its photo (the server records it that way), so it gets the photo reasons
        subject={post.PostType === 'NewPhoto' && post.PhotoId ? 'photo' : 'post'}
        onReasonSelected={(reason) => submitPolicyViolationForPost(post, reason)}
      />
    </>
  );
}
